import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, Loader2, Plus, ChevronDown, ChevronRight, Check, Sparkles, Trash2, RefreshCw } from "lucide-react";

const Spec = () => {
  const [catalog, setCatalog] = useState(null);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ board: "", level: "", subject: "" });
  const [tracking, setTracking] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  const [gapBusy, setGapBusy] = useState(false);

  const load = async () => {
    const [c, m] = await Promise.all([api.get("/specs/catalog"), api.get("/specs/mine")]);
    setCatalog(c.data);
    setMine(m.data || []);
    setForm((f) => ({
      board: f.board || c.data.boards[0],
      level: f.level || c.data.levels[0],
      subject: f.subject || c.data.subjects[0],
    }));
  };
  useEffect(() => { load(); }, []);

  const track = async (e) => {
    e.preventDefault();
    setTracking(true);
    try {
      const { data } = await api.post("/specs/track", form);
      toast.success(`Tracking ${data.board} ${data.level} ${data.subject}`);
      await load();
      openSpec(data.user_spec_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not track that specification");
    } finally { setTracking(false); }
  };

  const openSpec = async (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    try {
      const { data } = await api.get(`/specs/${id}`);
      setDetail(data);
      setOpenTopic(data.topics?.[0]?.id || null);
    } catch { toast.error("Failed to load specification"); }
  };

  const untrack = async (id) => {
    await api.delete(`/specs/${id}`);
    toast.message("Stopped tracking");
    setOpenId(null); setDetail(null);
    load();
  };

  const generateGaps = async (id) => {
    setGapBusy(true);
    try {
      await api.post(`/specs/${id}/revise-gaps`);
      toast.success("Gap revision notes generated — find them on your dashboard subject");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setGapBusy(false); }
  };

  if (!catalog) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="spec-page">
      <header className="mb-10">
        <div className="label-mono mb-3">/ exam spec</div>
        <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">
          Know exactly what&apos;s left.
        </h1>
        <p className="text-neutral-500 mt-3 text-[0.92rem] max-w-2xl">
          Pick your board and subject. Every lesson you record is matched against the specification, so you can
          see what&apos;s been taught, what hasn&apos;t, and revise only the gaps.
        </p>
      </header>

      <form onSubmit={track} className="glass-strong p-6 mb-10 grid md:grid-cols-4 gap-3 items-end" data-testid="spec-form">
        <Field label="board">
          <select className="input-cyber" value={form.board} onChange={(e)=>setForm({...form, board:e.target.value})} data-testid="spec-board">
            {catalog.boards.map((b) => <option key={b} value={b} className="bg-black">{b}</option>)}
          </select>
        </Field>
        <Field label="level">
          <select className="input-cyber" value={form.level} onChange={(e)=>setForm({...form, level:e.target.value})} data-testid="spec-level">
            {catalog.levels.map((b) => <option key={b} value={b} className="bg-black">{b}</option>)}
          </select>
        </Field>
        <Field label="subject">
          <select className="input-cyber" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} data-testid="spec-subject">
            {catalog.subjects.map((b) => <option key={b} value={b} className="bg-black">{b}</option>)}
          </select>
        </Field>
        <button className="btn-primary" disabled={tracking} data-testid="spec-track">
          {tracking ? <><Loader2 size={13} className="animate-spin"/> Building…</> : <><Plus size={13}/> Track spec</>}
        </button>
        {tracking && (
          <div className="md:col-span-4 text-[0.75rem] text-neutral-500">
            Building the specification and matching your existing lessons — this takes up to a minute.
          </div>
        )}
      </form>

      <section className="space-y-3">
        {mine.length === 0 && (
          <div className="text-neutral-500 text-[0.86rem] border border-dashed border-white/[0.08] p-6 rounded-[2px] text-center">
            No specification tracked yet.
          </div>
        )}
        {mine.map((s) => (
          <div key={s.user_spec_id} className="glass" data-testid={`spec-${s.user_spec_id}`}>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="label-mono mb-2 flex items-center gap-2"><Target size={11}/> {s.board} · {s.level}</div>
                  <div className="font-display text-xl tightest font-semibold">{s.subject}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl tightest font-semibold tabular" data-testid={`spec-pct-${s.user_spec_id}`}>{s.coverage_pct}%</div>
                  <div className="text-[0.72rem] text-neutral-500 tabular">{s.covered_points} / {s.total_points} points taught</div>
                </div>
              </div>
              <div className="mt-4 h-1.5 bg-white/[0.05] rounded-[1px] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-rose-500 transition-all duration-700" style={{ width: `${s.coverage_pct}%` }} />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button onClick={() => openSpec(s.user_spec_id)} className="btn-secondary text-[0.78rem] py-1.5 px-3" data-testid={`spec-open-${s.user_spec_id}`}>
                  {openId === s.user_spec_id ? <><ChevronDown size={12}/> Hide breakdown</> : <><ChevronRight size={12}/> Topic breakdown</>}
                </button>
                <button onClick={() => generateGaps(s.user_spec_id)} disabled={gapBusy} className="btn-primary text-[0.78rem] py-1.5 px-3" data-testid={`spec-gaps-${s.user_spec_id}`}>
                  {gapBusy ? <><Loader2 size={12} className="animate-spin"/> Writing…</> : <><Sparkles size={12}/> Revise my gaps</>}
                </button>
                <button onClick={() => untrack(s.user_spec_id)} className="btn-ghost text-[0.78rem] py-1.5 px-2 ml-auto" data-testid={`spec-untrack-${s.user_spec_id}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            </div>

            {openId === s.user_spec_id && (
              <div className="border-t border-white/[0.06] p-6" data-testid="spec-breakdown">
                {!detail && <div className="text-neutral-500 text-[0.84rem] flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> loading…</div>}
                {detail && detail.topics.map((t) => (
                  <div key={t.id} className="border border-white/[0.06] rounded-[2px] mb-2">
                    <button
                      onClick={() => setOpenTopic(openTopic === t.id ? null : t.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
                      data-testid={`topic-${t.id}`}
                    >
                      {openTopic === t.id ? <ChevronDown size={13} className="text-neutral-500 shrink-0"/> : <ChevronRight size={13} className="text-neutral-500 shrink-0"/>}
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.88rem] text-white truncate">{t.title}</div>
                        <div className="mt-1.5 h-0.5 bg-white/[0.06] rounded-[1px] overflow-hidden max-w-xs">
                          <div className={`h-full ${t.pct === 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${t.pct}%` }} />
                        </div>
                      </div>
                      <div className="text-[0.72rem] text-neutral-500 tabular shrink-0">{t.covered}/{t.total}</div>
                    </button>
                    {openTopic === t.id && (
                      <div className="px-4 pb-3 space-y-1.5">
                        {t.points.map((p) => (
                          <div key={p.id} className="flex items-start gap-2.5 text-[0.82rem]" data-testid={`point-${p.id}`}>
                            <span className={`mt-0.5 w-4 h-4 shrink-0 border flex items-center justify-center rounded-[2px] ${p.covered ? "border-emerald-500/50 bg-emerald-500/[0.10]" : "border-white/[0.10]"}`}>
                              {p.covered && <Check size={10} className="text-emerald-400"/>}
                            </span>
                            <span className={p.covered ? "text-neutral-300" : "text-neutral-500"}>
                              {p.code && <span className="font-mono text-[0.72rem] text-neutral-600 mr-1.5">{p.code}</span>}
                              {p.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {detail && (
                  <div className="text-[0.72rem] text-neutral-600 mt-4 flex items-center gap-1.5">
                    <RefreshCw size={11}/> Coverage updates automatically each time you record a {detail.subject} lesson.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="label-mono mb-2">{label}</div>
    {children}
  </div>
);

export default Spec;
